import React, { useState } from "react";
import { Sidenav, Nav, Modal, Button, SelectPicker, Form, Input, Toggle } from "rsuite";
import GearIcon from "@rsuite/icons/Gear";
import TreeIcon from "@rsuite/icons/Tree";
import RemindIcon from "@rsuite/icons/legacy/Remind";
import OffIcon from "@rsuite/icons/Off";
import EditIcon from "@rsuite/icons/Edit";
import SentToUserIcon from "@rsuite/icons/SentToUser";
import mapleLogo from "../../assets/images/maple-exp-light.png";
import mapleLogoDark from "../../assets/images/maple-top-dark.png";
import darkModeIcon from "../../assets/images/dark-mode.png";
import InfoOutlineIcon from "@rsuite/icons/InfoOutline";
import BarLineChartIcon from "@rsuite/icons/BarLineChart";
import PublicOpinionIcon from "@rsuite/icons/PublicOpinion";
import WechatOutlineIcon from '@rsuite/icons/WechatOutline';
import { useLocation, useNavigate } from "react-router";
import { queryPostRequest } from "../../api";
import { useTheme } from "../../context/ThemeContext";

import "rsuite/dist/rsuite.min.css";
import "./SideNavBar.css";

const grafanaUrl =
  window.REACT_APP_GRAFANA_VIDEOTRON_URL ||
  "https://maple-videotron-grafana.maple.comcast.net";

const SideNavBar = ({ isAuthorized, hasTabsWithQueries, sessionExpired }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [feedbackType, setFeedbackType] = React.useState("Bug");
  const [feedbackText, setFeedbackText] = React.useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const isOnQueryEditor = location.pathname === "/query-editor";

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleBackToEditor = () => setOpen(false);

  const handleFeedbackOpen = () => setFeedbackOpen(true);
  const handleFeedbackClose = () => setFeedbackOpen(false);

  const logout = () => {
    setOpen(false);
    localStorage.clear();
    try {
      sessionStorage.removeItem("maple.qe.state.v1");
    } catch {
      // ignore
    }
  };

  const handleAbout = () => {
    navigate("/about");
  };

  const handleQueryEditor = () => {
    if (!isOnQueryEditor) {
      navigate("/query-editor");
    }
  };

  const handlePipelines = () => {
    navigate("/pipelines");
  };

  const getUser = () => {
    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      return user;
    } catch {
      return null;
    }
  };

  const isVideotronUser = () => {
    const user = getUser();
    return user?.loginType === "xerxes-videotron";
  };

  const handleGrafanaLink = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleEventAnalyzer = () => {
    handleGrafanaLink(
      `${grafanaUrl}/d/-R_yQHNWk/events-analyzer-raw-data?orgId=1&from=now-5m&to=now&timezone=utc&var-partner=eetm9ifeb4mwwa&var-tier_name=edge&var-svc_type=linear&var-svc=$__all&var-ip=&var-domain=&var-path=&var-crc=$__all&var-pssc=$__all&var-host=$__all&var-uas=&var-ch_table=atsec_chi&var-uas_hack=uas_type&var-php=$__all&var-pqsn=&var-trace_id=&var-ptcl=$__all&var-ttms=800`,
    );
  };

  const handleKpiView = () => {
    handleGrafanaLink(
      `${grafanaUrl}/d/DtNwCVCGk/video-level-analyzer-raw-data?orgId=1&from=now-1h&to=now&timezone=utc&var-partner=eetm9ifeb4mwwa&var-tier_name=edge&var-svc_type=&var-svc=&var-ip=&var-domain=&var-path=&var-crc=$__all&var-pssc=$__all&var-host=$__all&var-uas=&var-ch_table=atsec_chi&var-uas_hack=uas_type&var-php=$__all&var-pqsn=&var-trace_id=&var-ptcl=$__all`,
    );
  };

  const handleAssetViewership = () => {
    handleGrafanaLink(
      `${grafanaUrl}/d/b8o-k3x7z/linear-asset-viewership?orgId=1&from=now-1h&to=now&timezone=utc&var-partner=eetm9ifeb4mwwa&var-cachegroup=$__all&var-svc=$__all&var-host=$__all&var-filter=cache_group&var-channel=&refresh=5m`,
    );
  };

  const handleIpExplorer = () => {
    handleGrafanaLink(
      `${grafanaUrl}/d/ceu3ykol4fo5cc/ip-explorer?orgId=1&from=now-6h&to=now&timezone=utc&var-client_ip=2607:fa48:c0:624::3&var-selected_time=`,
    );
  };

  const user = getUser();
  const userName = user?.name || "";

  const handleSubmitFeedback = async () => {
    const requestBody = {
      timestamp: new Date().toISOString(),
      name: userName || "Unknown",
      type: feedbackType,
      feedback: feedbackText,
    };

    try {
      await queryPostRequest("feedback", requestBody);
      setFeedbackText("");
      setFeedbackType("Bug");
      handleFeedbackClose();
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  return (
    <div className="nav-container" data-testid="side-nav-bar">
      <Sidenav expanded={expanded} className="sidenavstyle">
        <Sidenav.Body>
          <div className="logo-container">
            <img src={isDarkMode ? mapleLogoDark : mapleLogo} alt="Maple Logo" className="logo" />
            {expanded && <span className="maple-text">MAPLE</span>}
          </div>
          <Nav>
            <Nav.Item
              eventKey="1"
              icon={<EditIcon />}
              className={`navitem ${isOnQueryEditor ? "query-editor-navitem" : ""}`}
              onSelect={handleQueryEditor}
            >
              Query Editor
            </Nav.Item>
            {isAuthorized && (
              <Nav.Item
                eventKey="2"
                icon={<TreeIcon />}
                className="navitem"
                onSelect={handlePipelines}
              >
                Pipelines
              </Nav.Item>
            )}
            {isVideotronUser() && (
              <Nav.Menu
                placement="rightStart"
                eventKey="3"
                title="Grafana"
                icon={<BarLineChartIcon />}
                className="navitem"
              >
                <Nav.Item eventKey="3-1" onClick={handleEventAnalyzer}>
                  Event Analyzer View
                </Nav.Item>
                <Nav.Item eventKey="3-2" onClick={handleKpiView}>
                  KPI View
                </Nav.Item>
                <Nav.Item eventKey="3-3" onClick={handleAssetViewership}>
                  Asset Viewership View
                </Nav.Item>
                <Nav.Item eventKey="3-4" onClick={handleIpExplorer}>
                  IP Explorer View
                </Nav.Item>
              </Nav.Menu>
            )}
            <Nav.Menu
              placement="rightStart"
              eventKey="4"
              title="Settings"
              icon={<GearIcon />}
              className="navitem"
            >
              <Nav.Item eventKey="4-0" className="theme-toggle-nav-item">
                <div className="theme-toggle-row" onClick={(event) => event.stopPropagation()}>
                  <span className="theme-toggle-label">
                    <img src={darkModeIcon} alt="Dark mode" className="theme-toggle-icon" />
                    <span>Dark mode</span>
                  </span>
                  <Toggle
                    checked={isDarkMode}
                    size="sm"
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={toggleTheme}
                  />
                </div>
              </Nav.Item>
              <Nav.Item
                eventKey="4-1"
                className="about-maple"
                icon={<InfoOutlineIcon className="info-icon" />}
                onClick={handleAbout}
              >
                About Maple
              </Nav.Item>
            </Nav.Menu>
            {isAuthorized && (
              <Nav.Item
                eventKey="2-2"
                icon={<WechatOutlineIcon />}
                className="navitem"
                onClick={() => navigate("/feedback-table")}
              >
                View Feedback
              </Nav.Item>
            )}
              <Nav.Item
                eventKey="5"
                icon={<PublicOpinionIcon />}
                className="navitem"
                onSelect={handleFeedbackOpen}
              >
                Feedback
              </Nav.Item>
            <Nav.Item
              eventKey="6"
              icon={<SentToUserIcon />}
              className="navitem"
              href="https://comcast.enterprise.slack.com/archives/C06SRMMNE"
              target="_blank"
            >
              Contact
            </Nav.Item>
            <Nav.Item
              eventKey="7"
              icon={<OffIcon />}
              className="navitem"
              onSelect={handleOpen}
            >
              Logout
            </Nav.Item>
          </Nav>
        </Sidenav.Body>
        <Sidenav.Toggle onToggle={() => setExpanded(!expanded)} />
      </Sidenav>
      <Modal
        backdrop="static"
        open={feedbackOpen}
        onClose={handleFeedbackClose}
        className="feedback-modal"
        size="sm"
      >
        <Modal.Header>
          <Modal.Title className="feedback-title">Feedback</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form fluid>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <label style={{ width: 80, fontWeight: 600, margin: 0 }}>
                Name:
              </label>
              <Form.Control
                accepter={Input}
                value={userName}
                disabled
                className="feedback-name"
                style={{ flex: 1, minWidth: 0 }}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <label style={{ width: 80, fontWeight: 600, margin: 0 }}>
                Type:
              </label>
              <SelectPicker
                cleanable={false}
                searchable={false}
                placement="bottom"
                appearance="subtle"
                style={{ flex: 1, minWidth: 0 }}
                data={[
                  { label: "Bug", value: "Bug" },
                  { label: "Feature request", value: "Feature request" },
                  { label: "Comments", value: "Comments" },
                ]}
                value={feedbackType}
                onChange={(value) => setFeedbackType(value)}
              />
            </div>
            <Form.Group
              className="feedback-textarea"
              style={{ marginBottom: 0 }}
            >
              <Form.ControlLabel style={{ fontWeight: 600 }}>
                Feedback:
              </Form.ControlLabel>
              <Input
                as="textarea"
                rows={5}
                value={feedbackText}
                onChange={setFeedbackText}
                placeholder="Write your feedback..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleFeedbackClose} appearance="subtle">
            Cancel
          </Button>
          <Button onClick={handleSubmitFeedback} appearance="primary">
            Submit Feedback
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        backdrop="static"
        role="alertdialog"
        open={open && !sessionExpired}
        onClose={handleClose}
        size="xs"
        className={sessionExpired ? "" : "tight-modal"}
      >
        <Modal.Body>
          <div className="modal-content">
            <div className="modal-header">
              <RemindIcon className="modal-icon" />
              <h6 className="modal-title">
                {hasTabsWithQueries ? "Unsaved Queries" : "Confirm Logout"}
              </h6>
            </div>
            <p className="modal-message">
              {hasTabsWithQueries
                ? "You have unsaved queries. Do you want to save them before logging out?"
                : "Are you sure you want to logout?"}
            </p>
          </div>
        </Modal.Body>

        <Modal.Footer className="modal-footer">
          {hasTabsWithQueries ? (
            <>
              <Button
                onClick={handleBackToEditor}
                appearance="primary"
                className="modal-button tight"
                data-testid="unsaved-queries-back-button"
              >
                Back
              </Button>
              <Button
                onClick={logout}
                appearance="subtle"
                href="/login"
                className="modal-button tight"
              >
                Logout Anyway
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={logout}
                appearance="primary"
                href="/login"
                className="modal-button tight"
              >
                Logout
              </Button>
              <Button
                onClick={handleClose}
                appearance="subtle"
                className="modal-button tight"
              >
                Cancel
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SideNavBar;
